import { useState, useEffect, useCallback, useRef } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import Badge from '../../components/admin/Badge';

// Only these variants exist in the admin stylesheet.
const SEVERITY_VARIANT = { critical: 'rejected', high: 'suspended', normal: 'info', low: 'pending' };

const ACTOR_ICON = {
  admin: 'fa-solid fa-user',
  automation: 'fa-solid fa-robot',
  token: 'fa-solid fa-key',
  unknown: 'fa-solid fa-circle-question',
};

const CATEGORY_ICON = {
  'Access control': 'fa-solid fa-user-shield',
  Security: 'fa-solid fa-key',
  Subscriptions: 'fa-solid fa-credit-card',
  Infrastructure: 'fa-solid fa-server',
  Catalog: 'fa-solid fa-tags',
  Support: 'fa-solid fa-life-ring',
  Configuration: 'fa-solid fa-sliders',
  Automation: 'fa-solid fa-robot',
  Data: 'fa-solid fa-file-export',
  Other: 'fa-solid fa-circle-info',
};

function relativeTime(iso) {
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const EMPTY_FILTERS = { q: '', category: '', action: '', actor: '', from: '', to: '' };

function AdminAuditLog({ apiFetch, toast }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ actions: [], actors: [], categories: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const limit = 50;
  const debounce = useRef(null);

  const load = useCallback(async (nextOffset, f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(nextOffset) });
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await apiFetch(`/api/admin/audit-log?${params}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } catch { toast.error('Error', 'Failed to load audit log'); }
    finally { setLoading(false); }
  }, [apiFetch, toast]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/admin/audit-log/facets');
        if (res.ok) setFacets(await res.json());
      } catch { /* filters degrade to free-text search */ }
    })();
  }, [apiFetch]);

  // Search debounces; dropdowns and dates apply immediately.
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(0, filters), filters.q ? 350 : 0);
    return () => clearTimeout(debounce.current);
  }, [filters, load]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const dirty = Object.values(filters).some(Boolean);

  // When a category is picked, only offer the actions that belong to it.
  const visibleActions = filters.category
    ? facets.actions.filter(a => a.category === filters.category)
    : facets.actions;

  return (
    <div>
      <SectionHeader
        title="Audit Log"
        description="Every recorded change, who made it, and what it affected. Automation keys appear as robots."
        actions={
          <button className="admin-btn admin-btn--secondary" onClick={() => load(offset, filters)}>
            <i className="fa-solid fa-arrows-rotate" /> Refresh
          </button>
        }
      />

      <div className="admin-card" style={{ marginBottom: 16, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-text-muted)', fontSize: 13 }} />
          <input
            className="admin-input"
            style={{ width: '100%', paddingLeft: 34 }}
            placeholder="Search summaries, actors, targets, details…"
            value={filters.q}
            onChange={e => set('q', e.target.value)}
          />
        </div>

        <select className="admin-select" style={{ width: 170 }} value={filters.category}
          onChange={e => setFilters(f => ({ ...f, category: e.target.value, action: '' }))}>
          <option value="">All categories</option>
          {(facets.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select className="admin-select" style={{ width: 210 }} value={filters.action} onChange={e => set('action', e.target.value)}>
          <option value="">All actions</option>
          {visibleActions.map(a => <option key={a.value} value={a.value}>{a.label} ({a.count})</option>)}
        </select>

        <select className="admin-select" style={{ width: 210 }} value={filters.actor} onChange={e => set('actor', e.target.value)}>
          <option value="">All actors</option>
          {(facets.actors || []).map(a => (
            <option key={a.value} value={a.value}>{a.actor_type === 'automation' ? '🤖 ' : ''}{a.actor_name} ({a.count})</option>
          ))}
        </select>

        <input className="admin-input" type="date" style={{ width: 150 }} value={filters.from} onChange={e => set('from', e.target.value)} title="From date" />
        <input className="admin-input" type="date" style={{ width: 150 }} value={filters.to} onChange={e => set('to', e.target.value)} title="To date" />

        {dirty && (
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={() => setFilters(EMPTY_FILTERS)}>Clear</button>
        )}
      </div>

      <div className="admin-card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--admin-text-muted)' }}>
            <i className="fa-solid fa-spinner fa-spin" /> Loading…
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--admin-text-muted)' }}>
            <i className="fa-solid fa-clipboard-list" style={{ fontSize: 30, opacity: 0.4, display: 'block', marginBottom: 10 }} />
            {dirty ? 'No entries match these filters' : 'No audit log entries yet'}
          </div>
        ) : logs.map(row => {
          const open = expanded === row.id;
          const hasDetails = row.details && Object.keys(row.details).length > 0;
          return (
            <div key={row.id} style={{ borderBottom: '1px solid var(--admin-border)' }}>
              <div
                onClick={() => setExpanded(open ? null : row.id)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', cursor: hasDetails ? 'pointer' : 'default' }}
              >
                <i className={CATEGORY_ICON[row.category] || CATEGORY_ICON.Other}
                   style={{ width: 18, textAlign: 'center', marginTop: 3, color: 'var(--admin-text-muted)' }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>{row.summary}</div>
                  <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span title={`${row.actor_type} · ${row.admin_email}`}>
                      <i className={ACTOR_ICON[row.actor_type] || ACTOR_ICON.unknown} style={{ marginRight: 5 }} />{row.actor_name}
                    </span>
                    {row.target_type && <span>· {row.target_type} <code>{row.target_id}</code></span>}
                    {row.ip_address && row.ip_address !== 'unknown' && <span>· {row.ip_address}</span>}
                    {hasDetails && <span style={{ opacity: 0.75 }}>· <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10 }} /> details</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <Badge variant={SEVERITY_VARIANT[row.severity] || 'info'}>{row.category}</Badge>
                  <span style={{ fontSize: 12, color: 'var(--admin-text-muted)', whiteSpace: 'nowrap', minWidth: 66, textAlign: 'right' }}
                        title={new Date(row.created_at).toLocaleString()}>
                    {relativeTime(row.created_at)}
                  </span>
                </div>
              </div>

              {open && hasDetails && (
                <div style={{ padding: '0 16px 14px 46px' }}>
                  <table style={{ fontSize: 12, borderCollapse: 'collapse' }}>
                    <tbody>
                      {Object.entries(row.details).map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ padding: '3px 14px 3px 0', color: 'var(--admin-text-muted)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
                          <td style={{ padding: '3px 0', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ padding: '3px 14px 3px 0', color: 'var(--admin-text-muted)', verticalAlign: 'top' }}>action</td>
                        <td style={{ padding: '3px 0', fontFamily: 'monospace' }}>{row.action}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, padding: '0 4px' }}>
          <span style={{ color: 'var(--admin-text-muted)', fontSize: 13 }}>
            Showing {offset + 1}–{Math.min(offset + logs.length, total)} of {total.toLocaleString()}
          </span>
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--secondary admin-btn--sm" disabled={offset === 0}
              onClick={() => load(Math.max(0, offset - limit), filters)}>
              <i className="fa-solid fa-chevron-left" /> Previous
            </button>
            <button className="admin-btn admin-btn--secondary admin-btn--sm" disabled={offset + limit >= total}
              onClick={() => load(offset + limit, filters)}>
              Next <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAuditLog;
